/*******************************************************************************
 * Licensed Materials - Property of IBM
 * IBM Cloud Code Engine, 5900-AB0
 * © Copyright IBM Corp. 2020, 2022
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 ******************************************************************************/

import $ from 'jquery';
import React from 'react';
import { createRoot } from 'react-dom/client';
import utils from '../api/utils';
import { init as i18nInit, t } from './i18n';
import { pageEvent } from './segment';
import win from './window';
import clgNlsKeys from './NlsKeys';

const env = window.armada;
const config = env.config;

interface ILocalStorage {
  key: (index: number) => string;
  length: number;
  getItem: (key: string) => string;
  removeItem: (key: string) => void;
}

interface ITestInterface {
  cleanLocalStorage: () => any;
  exposeGlobalLibs: () => any;
}

// Export the "private" methods for unit testing
const _: ITestInterface = {
  // Clean up expired local storage
  cleanLocalStorage: () => {
    try {
      const storage: ILocalStorage = win.get('localStorage');
      const now = new Date().getTime();
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (/^bluemix_ui:v1:/.test(key)) {
          const value = JSON.parse(storage.getItem(key));
          // Clear storage if it's expired
          if (value && value.expires && value.expires < now) {
            storage.removeItem(key);
          }
        }
      }
    } catch (e) {
      console.error('Error cleaning up local storage:', e);
    }
  },

  exposeGlobalLibs: () => {
    window.armada.libs = {
      React,
      // TODO: (react18) check if we can just remove this -> ReactDom,
    };
  },
};

const init = (Component) => {
  _.exposeGlobalLibs();

  // Convert any boolean string flags to actual booleans
  Object.keys(env).forEach((k) => {
    const value = env[k];
    if (value === 'true') {
      env[k] = true;
    } else if (value === 'false') {
      env[k] = false;
    }
  });

  // Prepare translation and then render the app
  i18nInit().then(() => {
    const container = document.getElementById('container');
    const root = createRoot(container!); // React 18 docs say we need to do non-null-assertion here!
    root.render(Component);
  });

  // When switching accounts, if the user is viewing a resource associated with an account
  // then navigate back to the top level page, otherwise reload the current page. In either
  // case we make a request to the server so that the selected account is persisted in the
  // user session before the page is reloaded.
  document.addEventListener('acctChanged', () => {
    $('#container').addClass('wait');
    utils.doGet({ url: config.proxyRoot }).always(() => {
      const path = win
        .get('location.pathname')
        .replace(/\/$/, '')
        .replace(/^\/[^\/]+\//, '')
        .split('/');
      if (path[0] === 'clusters' && path.length > 1) {
        win.set('location', `${config.proxyRoot}clusters`);
      } else if (path[0] === 'catalog' && path[1] === 'cluster' && path[2] !== 'create') {
        win.set('location', `${config.proxyRoot}clusters`);
      } else if (path[0] === 'registry' && path[1] !== 'main') {
        win.set('location', `${config.proxyRoot}registry/main/images`);
      } else {
        win.get('location').reload();
      }
    });
  });

  // Setup Carbon autotrack to produce segment events for analytics. Use in manual mode to
  // avoid too many client-side events being sent and update the class configuration in order
  // to capture elements not covered by autotrack by default.
  win.waitForWindowProp('autoTrack', function () {
    window.addEventListener('load', () => {
      const autoTrack = win.get('autoTrack');
      if (autoTrack) {
        autoTrack.setManualMode(true);
        autoTrack.setDevMode(false);
        autoTrack.updateClassConfig([
          '.cds--tile',
          '.clg-fleet-commands .cds--text-area',
          '.clg-fleet-arguments .cds--text-area',
          '.cds--radio-button',
          '.clg-create-fleet-tasks--batch-name .cds--text-input',
        ]);
      }
    });
  });

  _.cleanLocalStorage();
};

export const setPageTitle = (titles: string[]) => {
  let title = t(clgNlsKeys.clg.page.create.project.platform.ibmcloud.name);
  if (!titles || titles.length === 0) {
    // No subtitles, must be the landing page
    title = `${t(clgNlsKeys.offering.title)} - ${t(clgNlsKeys.clg.breadcrumb.containers)} - ${title}`;
  } else {
    // Otherwise we get the list of translated subtitles handed in to us, in hierarchical order (i.e., serverless, project name,  etc)
    titles.forEach((subtitle) => {
      title = `${subtitle} - ${title}`;
    });
  }
  // set the page title
  document.title = title;
};

/**
 * This helper function triggers common actions once the user arrived on the page (e.g. it updates the document title)
 * @param pageTitle The NLS key (as string) that we use for the tracking event
 * @param projectId the id of the project will be used as the instanceGuid property for segement
 */
export const arrivedOnPage = (pageTitle: string, titles: string[], projectId?: string) => {
  // scroll to the top of the page
  window.scrollTo(0, 0);

  // track the event via segment
  pageEvent('Offering Interface', pageTitle, projectId);

  // set the page title
  setPageTitle(titles);
};

export default { arrivedOnPage, init, setPageTitle, _ };
